import React, { useCallback, useState } from "react";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { WhatsappPinDialogScreenToParametersMap } from "../../types";
import { WhatsappPinDialogContent } from "../../components/WhatsappPinDialogContent";
import { WhatsappPinDialogFooter } from "../../components/WhatsappPinDialogFooter";
import { arePinsEqual, isPinValid } from "../../utils";

export const ConfirmPinScreen: React.FC<{
  pin: string;
  onClose: () => void;
}> = ({ pin, onClose }) => {
  const { t } = useTranslations();
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { transitionTo } =
    useScreenTransitionManager<WhatsappPinDialogScreenToParametersMap>();
  const [confirmPin, setConfirmPin] = useState("");

  const [setWhatsappPin, { isLoading }] =
    integrationsApi.useSetWhatsappPinMutation();

  const handleSetWhatsappPin = useCallback(async () => {
    const resp = await setWhatsappPin({
      entityId,
      pin,
      meta: {
        successMessage: t({
          id: "client-support-page.two-factor-authentication-successfully-enabled",
          defaultMessage: "Two-factor authentication was successfully enabled",
        }),
      },
    });

    if (resp && "data" in resp) {
      onClose();
    }
  }, [entityId, onClose, pin, setWhatsappPin, t]);

  const isValid = isPinValid(confirmPin);
  const areEqual = arePinsEqual(confirmPin, pin);

  return (
    <>
      <WhatsappPinDialogContent
        title={t({
          id: "client-support-page.confirm-pin",
          defaultMessage: "Confirm PIN",
        })}
        subtitle={t({
          id: "client-support-page.repeat-pin",
          defaultMessage: "Repeat the PIN, to make sure its correct",
        })}
        pin={confirmPin}
        onPinChange={setConfirmPin}
        errorMessage={
          isValid && !areEqual
            ? t({
                id: "client-support-page.pin-is-not-correct",
                defaultMessage: "PIN is not correct!",
              })
            : undefined
        }
        successMessage={
          areEqual
            ? t({
                id: "client-support-page.pin-is-matching",
                defaultMessage: "PIN is matching!",
              })
            : undefined
        }
      />
      <WhatsappPinDialogFooter
        buttons={[
          {
            text: t({
              id: "common.back",
              defaultMessage: "Back",
            }),
            color: "grey",
            isDisabled: isLoading,
            onClick: () => transitionTo({ name: "createPin" }),
          },
          {
            text: t({
              id: "common.save",
              defaultMessage: "Save",
            }),
            onClick: handleSetWhatsappPin,
            isDisabled: !areEqual || isLoading,
          },
        ]}
      />
    </>
  );
};
